/**
 * A small hand-rolled tween engine — no animation library. [P6-anim]
 *
 * Every tween is skippable and interruptible: `skipAll()` jumps every running tween straight to its end
 * state, which is what lets a reconnecting or impatient player skip the backlog instead of the game
 * blocking on an animation. `prefers-reduced-motion` is honoured by `scaleDuration`, which every caller
 * routes its requested duration through.
 */

export type Easing = (t: number) => number;

export const linear: Easing = (t) => t;
export const easeOutCubic: Easing = (t) => 1 - Math.pow(1 - t, 3);
export const easeInCubic: Easing = (t) => t * t * t;
export const easeInOutQuad: Easing = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
export const easeOutBack: Easing = (t) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

const reducedMotionQuery = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

/** Route every animation duration through this — collapses to near-instant under reduced motion. */
export function scaleDuration(ms: number): number {
  return reducedMotionQuery?.matches ? Math.min(ms, 40) : ms;
}

export interface Tween {
  /** Advances the tween; returns false once it has completed and should be dropped. */
  update(dtSeconds: number): boolean;
  /** Jumps immediately to the end state and fires completion. */
  skip(): void;
}

class ValueTween implements Tween {
  private elapsed = 0;
  private done = false;
  constructor(
    private readonly durationMs: number,
    private readonly onUpdate: (t: number) => void,
    private readonly easing: Easing,
    private readonly onComplete?: () => void,
  ) {}

  update(dtSeconds: number): boolean {
    if (this.done) return false;
    this.elapsed += dtSeconds * 1000;
    const t = Math.min(1, this.durationMs <= 0 ? 1 : this.elapsed / this.durationMs);
    this.onUpdate(this.easing(t));
    if (t >= 1) {
      this.done = true;
      this.onComplete?.();
      return false;
    }
    return true;
  }

  skip(): void {
    if (this.done) return;
    this.done = true;
    this.onUpdate(this.easing(1));
    this.onComplete?.();
  }
}

export function tween(
  durationMs: number,
  onUpdate: (t: number) => void,
  opts: { easing?: Easing; onComplete?: () => void } = {},
): Tween {
  return new ValueTween(scaleDuration(durationMs), onUpdate, opts.easing ?? easeOutCubic, opts.onComplete);
}

/** A sequence of tween factories, run one after another. Skipping the group skips straight through. */
export function sequence(factories: readonly (() => Tween)[]): Tween {
  let index = 0;
  let current: Tween | null = factories.length > 0 ? (factories[0] as () => Tween)() : null;
  return {
    update(dt) {
      while (current) {
        const stillRunning = current.update(dt);
        if (stillRunning) return true;
        index += 1;
        current = index < factories.length ? (factories[index] as () => Tween)() : null;
      }
      return false;
    },
    skip() {
      while (current) {
        current.skip();
        index += 1;
        current = index < factories.length ? (factories[index] as () => Tween)() : null;
      }
    },
  };
}

export class TweenGroup {
  private readonly tweens = new Set<Tween>();

  add(t: Tween): Tween {
    this.tweens.add(t);
    return t;
  }

  update(dtSeconds: number): void {
    for (const t of [...this.tweens]) {
      if (!t.update(dtSeconds)) this.tweens.delete(t);
    }
  }

  /** Jumps every running tween to completion — the "never block on an animation" escape hatch. */
  skipAll(): void {
    for (const t of [...this.tweens]) t.skip();
    this.tweens.clear();
  }

  get size(): number {
    return this.tweens.size;
  }
}
